/////////////////////////////////////////////////////////////////////////////////////////////////////////
// 화일명 : companyUtil.ts
// 설명 : 로그인 회사 선택(SaaS 멀티테넌시, 2026-09-26)
//  - 회사 식별은 나중에 도메인(예: a.xga.co.kr)으로 바꿀 예정이고, 지금은 로그인 화면의 회사 콤보로 고름.
//  - 목록은 서버(apiCompanyList - 컨트롤 DB ZT_TENANT의 사용 중인 회사)에서 받음. 회사등록(ZX102M1)에서 회사를 추가하면 자동으로 나타남.
//    받아온 목록은 localStorage(coList)에 저장해 두었다가 서버에 연결하지 못하면 그것을 씀(저장된 것도 없으면 운영사만).
//  - 고른 회사는 localStorage(coCd)에 저장하고, axiosSetup이 모든 요청에 X-Co-Cd 헤더로 붙여 보냄
//    (로그인 전 요청은 이 회사 DB로, 로그인 후에는 토큰의 회사와 같아야 함 - 다르면 서버가 다시 로그인시킴).
/////////////////////////////////////////////////////////////////////////////////////////////////////////
import axios from 'axios';
import { API_BASE_URL } from '../config'; // API 기본 URL

export interface LoginCompany { coCd: string; coNm: string }

const KEY = 'coCd';
const LIST_KEY = 'coList';
const FALLBACK: LoginCompany[] = [{ coCd: 'PGA00000', coNm: '운영사' }];   // 서버 목록도 저장된 목록도 없을 때(운영사 = 기본 회사)

function readCachedList(): LoginCompany[] {
  try {
    const arr = JSON.parse(localStorage.getItem(LIST_KEY) || '[]');
    if (Array.isArray(arr) && arr.length > 0 && arr.every(c => c && typeof c.coCd === 'string' && typeof c.coNm === 'string')) return arr;
  } catch { /* 저장소 접근/파싱 실패 */ }
  return [];
}

let companies: LoginCompany[] = readCachedList().length > 0 ? readCachedList() : FALLBACK;

/** 지금 알고 있는 회사 목록(서버에서 받은 것 > 저장된 것 > 운영사만) */
export function getLoginCompanies(): LoginCompany[] { return companies; }

/** 서버에서 회사 목록을 받아 갱신 - 실패하면 지금 목록을 그대로 돌려줌
 *  겸사겸사 브라우저 탭 제목(document.title)도 로그인 전부터 solutionTitle(.env)로 맞춰줌 - 로그인화면이
 *  마운트 시 무조건 호출하는 유일한 공개 API라서(로그인 후 mainFrame과 같은 값, apiMainMenuTree 참고). */
export async function loadLoginCompanies(): Promise<LoginCompany[]> {
  try {
    const res = await axios.post(`${API_BASE_URL}/com/apiCompanyList`, {});
    const list = res.data?.rsResults;
    if (Array.isArray(list) && list.length > 0) {
      companies = list.map((c: any) => ({ coCd: String(c.coCd), coNm: String(c.coNm) }));
      try { localStorage.setItem(LIST_KEY, JSON.stringify(companies)); } catch { /* 저장 실패는 무시 */ }
    }
    const solutionTitle = res.data?.solutionTitle;
    if (solutionTitle) document.title = solutionTitle;
  } catch { /* 서버에 연결하지 못하면 기존 목록 사용 */ }
  return companies;
}

/** 선택된 회사코드 - 저장값이 없거나 목록에 없으면 첫 번째 회사 */
export function getSelectedCoCd(): string {
  try {
    const saved = localStorage.getItem(KEY) || '';
    if (companies.some(c => c.coCd === saved)) return saved;
  } catch { /* 저장소 접근 실패 */ }
  return companies[0]?.coCd || '';
}

export function setSelectedCoCd(coCd: string): void {
  try { localStorage.setItem(KEY, coCd); } catch { /* 저장소 접근 실패는 무시 */ }
}

export function getCompanyName(coCd: string): string {
  return companies.find(c => c.coCd === coCd)?.coNm || coCd;
}
